import React, { ChangeEvent, useState, useEffect } from 'react';
import { DetalleOT, Causa, EvidenciaImg, CatalogCategory, CatalogSubcategory, CatalogDetail } from '../../types/ot.types';

interface Step3Props {
    detallesOT: DetalleOT[];
    causas: Causa[];
    categories: CatalogCategory[];
    subcategoriesCache: Record<string, CatalogSubcategory[]>;
    detailsCache: Record<string, CatalogDetail[]>;
    updateDetalle: (id: number, field: keyof DetalleOT, value: any) => void;
    deleteDetalle: (id: number) => void;
    addNewConcept: () => void;
    fillSubcategory: (detalleId: number, idCategoria: string) => void;
    fillDetailSubcategory: (detalleId: number, idCategoria: string, idSubcategoria: string) => void;
    archivoCotizacion: EvidenciaImg | undefined;
    setArchivoCotizacion: React.Dispatch<React.SetStateAction<EvidenciaImg | undefined>>;
    fechaEstimadaEntrega: string;
    setFechaEstimadaEntrega: React.Dispatch<React.SetStateAction<string>>;
    isCompleted?: boolean;
    minFechaEstimadaEntrega?: string;
}

export const Step3Diagnostico: React.FC<Step3Props> = ({
    detallesOT, causas, categories, subcategoriesCache, detailsCache,
    updateDetalle, deleteDetalle, addNewConcept, fillSubcategory, fillDetailSubcategory,
    archivoCotizacion, setArchivoCotizacion, fechaEstimadaEntrega, setFechaEstimadaEntrega,
    isCompleted = false, minFechaEstimadaEntrega,
}) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});

    useEffect(() => {
        if (archivoCotizacion?.url) {
            setPdfUrl(archivoCotizacion.url);
        } else {
            setPdfUrl(null);
        }
    }, [archivoCotizacion]);

    const updateAppraisalFile = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPdfUrl(URL.createObjectURL(file));
            let appraisalFile: EvidenciaImg = {
                "id": "1",
                "url": URL.createObjectURL(file),
                "file": file,
                "tipo": "pdf",
                "nombreOriginal": file['name']
            };
            setArchivoCotizacion(appraisalFile);
        }
    }

    const stopSubmit = (e: any) => {
        e.preventDefault();
    }

    const toggleCollapse = (id: number) => {
        setCollapsedItems(prev => {
            const isCurrentlyCollapsed = prev[id] ?? isCompleted;
            return { ...prev, [id]: !isCurrentlyCollapsed };
        });
    }

    const granTotalAcumulado = detallesOT.reduce((acc, detalle) => {
        const cantidad = Number.parseFloat(detalle.cantidad.toString()) || 0;
        const precio_sin_iva = Number.parseFloat(detalle.precio_unitario_sin_iva.toString()) || 0;
        let iva_rate = (Number.parseFloat(detalle.porcentaje_iva.toString()) || 0) / 100;
        let isr_rate = (Number.parseFloat(detalle.porcentaje_isr?.toString()) || 0) / 100;

        const subtotal_calculado = cantidad * precio_sin_iva;
        const monto_iva_calculado = subtotal_calculado * iva_rate;
        const monto_isr_calculado = subtotal_calculado * isr_rate;
        const total_cotizado = subtotal_calculado + monto_iva_calculado - monto_isr_calculado;

        return acc + total_cotizado;
    }, 0);

    return (
        <form id="create-diagnosis" onSubmit={stopSubmit}>
            <div className="row mb-4">
                <div id="ot-step3-fecha-entrega" className="col-lg-4 col-md-6">
                    <label className="fw-bold mb-1 text-primary">Fecha Estimada de Entrega (Taller) *</label>
                    <input
                        type="datetime-local"
                        className="form-control shadow-sm border-primary"
                        required
                        value={fechaEstimadaEntrega}
                        disabled={isCompleted}
                        min={minFechaEstimadaEntrega}
                        onChange={(e) => setFechaEstimadaEntrega(e.target.value)}
                    />
                </div>
            </div>

            <div className="row gx-4 text-start">
                <div className="col-xl-4 mb-3">
                    <div id="ot-step3-cotizacion" className="card border-light shadow-sm h-100">
                        <div className="card-header d-flex flex-column gap-2 py-2">
                            <span className="fw-bold"><i className="fas fa-file-pdf text-danger me-2"></i> Documento de Cotización *</span>
                            {!isCompleted ? (
                                <input type="file" className="form-control form-control-sm w-100" required={!archivoCotizacion} accept="application/pdf" onChange={updateAppraisalFile} />
                            ) : (
                                <span className="badge bg-success w-100 py-2"><i className="fas fa-check-circle me-1"></i> Documento cargado exitosamente</span>
                            )}
                        </div>
                        <div className="card-body p-0 bg-secondary bg-opacity-10 d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                            {pdfUrl ? <embed src={pdfUrl} type="application/pdf" width="100%" height="600px" /> : <div className="text-muted text-center p-4"><i className="fas fa-cloud-upload-alt fa-3x mb-2"></i><p>Suba el PDF de cotización.</p></div>}
                        </div>
                    </div>
                </div>

                <div id="ot-step3-conceptos" className="col-xl-8 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0 text-primary"><i className="fas fa-list-ul me-2"></i> Desglose de Diagnóstico</h6>
                        {!isCompleted && (
                            <button type='button' className="btn btn-sm btn-primary rounded-pill shadow-sm" onClick={addNewConcept}>
                                <i className="fas fa-plus me-1"></i> Añadir Concepto
                            </button>
                        )}
                    </div>

                    {detallesOT.map((detalle: any, idx: number) => {
                        const cantidad = Number.parseFloat(detalle.cantidad.toString()) || 0;
                        const precio_sin_iva = Number.parseFloat(detalle.precio_unitario_sin_iva.toString()) || 0;
                        const iva_rate = (Number.parseFloat(detalle.porcentaje_iva.toString()) || 0) / 100;
                        const isr_rate = (Number.parseFloat(detalle.porcentaje_isr?.toString()) || 0) / 100;

                        const subtotal_calculado = cantidad * precio_sin_iva;
                        const monto_iva_calculado = subtotal_calculado * iva_rate;
                        const monto_isr_calculado = subtotal_calculado * isr_rate;
                        const total_cotizado = subtotal_calculado + monto_iva_calculado - monto_isr_calculado;

                        const isCollapsed = collapsedItems[detalle.id] ?? isCompleted;

                        const catLabel = categories.find(c => c.id == detalle.categoria)?.nombre || 'Sin Categoría';
                        const subLabel = subcategoriesCache[detalle.categoria]?.find(s => s.clave_subcategoria == detalle.subcategoria)?.subcategoria || 'Sin Subcategoría';
                        const detLabel = detailsCache[`${detalle.categoria}-${detalle.subcategoria}`]?.find(d => d.clave_subcategoria_detalle == detalle.subcategoriadetalle)?.titulo || 'Sin Detalle';

                        return (
                            <div key={detalle.id} className="card border-secondary border-opacity-25 shadow-sm mb-3">
                                <div className="card-header py-2 d-flex justify-content-between align-items-center" >
                                    <span className="fw-bold">Concepto #{idx + 1}</span>
                                    <div>
                                        <button type='button' className="btn btn-sm btn-outline-secondary py-0 px-2 me-2" onClick={() => toggleCollapse(detalle.id)} title={isCollapsed ? "Expandir" : "Minimizar"}>
                                            <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}></i>
                                        </button>
                                        {!isCompleted && detallesOT.length > 1 && (
                                            <button type='button' className="btn btn-sm btn-outline-danger py-0 px-2" onClick={() => deleteDetalle(detalle.id)}><i className="fas fa-trash-alt"></i></button>
                                        )}
                                    </div>
                                </div>

                                {isCollapsed ? (
                                    <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="text-muted text-truncate me-3" style={{ fontSize: '0.85rem' }}>
                                                <strong>{catLabel}</strong> &gt; {subLabel} &gt; {detLabel} &gt; <span className="fst-italic">{detalle.observaciones || 'Sin observaciones'}</span>
                                            </div>
                                            <div className="fw-bold text-success flex-shrink-0">
                                                Total: $ {total_cotizado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card-body p-3" style={{ fontSize: '0.85rem' }}>
                                        <div className="row g-2 mb-3">
                                            <div className="col-md-4">
                                                <label className="fw-bold mb-1">Categoría</label>
                                                <select className="form-select form-select-sm" required disabled={isCompleted} value={detalle.categoria} onChange={e => fillSubcategory(detalle.id, e.target.value)}>
                                                    <option value="">Elegir categoría...</option>
                                                    {categories.map((cat: any) => <option key={cat.id} value={cat.id}>{cat.nombre}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold mb-1">Subcategoría</label>
                                                <select className="form-select form-select-sm" required disabled={isCompleted} value={detalle.subcategoria} onChange={e => fillDetailSubcategory(detalle.id, detalle.categoria, e.target.value)}>
                                                    <option value="">Elegir subcategoría...</option>
                                                    {subcategoriesCache[detalle.categoria]?.map((sub: any) => <option key={sub.clave_subcategoria} value={sub.clave_subcategoria}>{sub.subcategoria}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold mb-1">Subcategoría detalle</label>
                                                <select className="form-select form-select-sm" required disabled={isCompleted} value={detalle.subcategoriadetalle} onChange={e => updateDetalle(detalle.id, 'subcategoriadetalle', e.target.value)}>
                                                    <option value="">Elegir detalle...</option>
                                                    {detailsCache[`${detalle.categoria}-${detalle.subcategoria}`]?.map((det: any) => <option key={det.clave_subcategoria_detalle} value={det.clave_subcategoria_detalle}>{det.titulo}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="row g-2 mb-3">
                                            <div className="col-md-5">
                                                <label className="fw-bold mb-1">Observaciones puntuales</label>
                                                <input type="text" className="form-control form-control-sm" disabled={isCompleted} placeholder="Ej: Las balatas rechinaban..." value={detalle.observaciones} onChange={e => updateDetalle(detalle.id, 'observaciones', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold mb-1">Causa del concepto</label>
                                                <select className="form-select form-select-sm" required disabled={isCompleted} value={detalle.causa} onChange={e => updateDetalle(detalle.id, 'causa', e.target.value)}>
                                                    <option value="">Elegir causa...</option>
                                                    {causas.length > 0 && causas.map((c: any) => <option key={c.clave_causa} value={c.clave_causa}>{c.causa}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-3 d-flex align-items-end mb-1">
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" id={`cobroCliente_${idx}`} disabled={isCompleted} checked={detalle.pago_por_cliente} onChange={e => updateDetalle(detalle.id, 'pago_por_cliente', e.target.checked)} />
                                                    <label className="form-check-label fw-bold text-danger" htmlFor={`cobroCliente_${idx}`}>¿Lo cubre el cliente?</label>
                                                </div>
                                            </div>
                                        </div>

                                        <h6 className="border-bottom pb-1 text-secondary mt-4 fw-bold">Costos (Captura SIN IVA)</h6>
                                        <div className="row g-2 mb-2">
                                            <div className="col-md-2">
                                                <label className="text-primary fw-bold">Cantidad</label>
                                                <input type="number" className="form-control form-control-sm border-primary" required disabled={isCompleted} value={detalle.cantidad} min={1} step="any" onChange={e => updateDetalle(detalle.id, 'cantidad', e.target.value)} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="text-primary fw-bold">Precio Unit. (SIN IVA)</label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text border-primary">$</span>
                                                    <input type="number" className="form-control form-control-sm border-primary" required disabled={isCompleted} value={detalle.precio_unitario_sin_iva} step="any" onChange={e => updateDetalle(detalle.id, 'precio_unitario_sin_iva', e.target.value)} />
                                                </div>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="fw-bold">Tasa IVA</label>
                                                <select className="form-select form-select-sm" required disabled={isCompleted} value={detalle.porcentaje_iva} onChange={e => updateDetalle(detalle.id, 'porcentaje_iva', e.target.value)}>
                                                    <option value="16">16%</option><option value="8">8%</option><option value="0">0%</option>
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="fw-bold">Retención ISR</label>
                                                <select className="form-select form-select-sm border-danger text-danger" required disabled={isCompleted} value={detalle.porcentaje_isr || 0} onChange={e => updateDetalle(detalle.id, 'porcentaje_isr', e.target.value)}>
                                                    <option value="0">0% (P. Moral)</option>
                                                    <option value="1.25">1.25% (RESICO)</option>
                                                    <option value="10">10% (P. Física)</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* DESGLOSE VISUAL (4 columnas) */}
                                        <div className="row g-2 p-2 rounded border border-light mt-2 align-items-center">
                                            <div className="col-md-3">
                                                <label className="text-muted small mb-0 d-block">Subtotal</label>
                                                <span className="fw-bold">$ {subtotal_calculado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="text-muted small mb-0 d-block">+ IVA</label>
                                                <span className="fw-bold">$ {monto_iva_calculado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="text-danger small mb-0 d-block">- Retención ISR</label>
                                                <span className="fw-bold text-danger">$ {monto_isr_calculado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="col-md-3 text-end border-start">
                                                <label className="text-success small fw-bold mb-0 d-block">Total del Concepto</label>
                                                <span className="fw-bold text-success fs-6">$ {total_cotizado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="card border-success mt-4 shadow">
                        <div className="card-body p-3 d-flex justify-content-between align-items-center bg-success bg-opacity-10">
                            <h5 className="mb-0 fw-bold text-success"><i className="fas fa-calculator me-2"></i>Gran Total Acumulado (Cotización)</h5>
                            <h4 className="mb-0 fw-bold text-success">$ {granTotalAcumulado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};